import { useEffect, useRef, useState, type PointerEvent } from 'react';

/** The saved avatar is a small square; the round display crops the corners. */
const OUTPUT = 160;
const MAX_ZOOM = 3;

interface Offset {
  x: number;
  y: number;
}

/**
 * Pick the square of a photo that becomes your avatar. Drag to move, zoom to
 * frame; the crop you see (inside the circle) is exactly what gets saved. All of
 * it runs on-device — the image is drawn to a 160px canvas here and only that
 * small JPEG ever leaves the browser.
 */
export function AvatarCropper({
  file,
  saving = false,
  uploadError = null,
  onCancel,
  onConfirm,
}: {
  file: File;
  saving?: boolean;
  /** A failed upload from the parent, shown by the buttons so a retry is obvious. */
  uploadError?: string | null;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
}) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [vp, setVp] = useState(0); // viewport size, CSS px (square)
  const [base, setBase] = useState(1); // the scale that just covers the viewport
  const [zoom, setZoom] = useState(1); // 1 = cover, up to MAX_ZOOM
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 }); // image top-left, px
  const [error, setError] = useState<string | null>(null);

  const viewportRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  const scale = base * zoom;

  // Load the picked file. The `cancelled` guard matters: React re-runs effects
  // (StrictMode in dev), and the cleanup revokes the object URL — without the
  // guard, a superseded load's onerror would fire against the revoked URL and
  // wrongly flash "couldn't read that image" over the load that did succeed.
  useEffect(() => {
    let cancelled = false;
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setImg(image);
    };
    image.onerror = () => {
      if (!cancelled) setError('Could not read that image');
    };
    image.src = url;
    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Track the viewport's real pixel size so the crop math is display-agnostic.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setVp(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit the image to cover the viewport and center it, whenever either changes.
  useEffect(() => {
    if (!img || !vp) return;
    const b = Math.max(vp / img.naturalWidth, vp / img.naturalHeight);
    setBase(b);
    setZoom(1);
    setOffset({
      x: (vp - img.naturalWidth * b) / 2,
      y: (vp - img.naturalHeight * b) / 2,
    });
  }, [img, vp]);

  // Keep the image covering the viewport — never let a gap show at an edge.
  const clamp = (o: Offset, s: number): Offset => {
    if (!img || !vp) return o;
    return {
      x: Math.min(0, Math.max(vp - img.naturalWidth * s, o.x)),
      y: Math.min(0, Math.max(vp - img.naturalHeight * s, o.y)),
    };
  };

  // Zoom around the viewport's center so framing doesn't jump.
  const changeZoom = (z: number) => {
    if (!img || !vp) {
      setZoom(z);
      return;
    }
    const sOld = base * zoom;
    const sNew = base * z;
    const cx = (vp / 2 - offset.x) / sOld;
    const cy = (vp / 2 - offset.y) / sOld;
    setZoom(z);
    setOffset(clamp({ x: vp / 2 - cx * sNew, y: vp / 2 - cy * sNew }, sNew));
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setOffset(clamp({ x: d.ox + (e.clientX - d.px), y: d.oy + (e.clientY - d.py) }, scale));
  };
  const endDrag = () => {
    drag.current = null;
  };

  const confirm = () => {
    if (!img || !vp) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return setError('Canvas unavailable');
    // The source square that the viewport currently frames.
    const sSize = vp / scale;
    ctx.drawImage(img, -offset.x / scale, -offset.y / scale, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
    onConfirm(canvas.toDataURL('image/jpeg', 0.82));
  };

  if (error) {
    return (
      <div className="card space-y-3 p-6">
        <p className="font-mono text-xs text-blood">{error}</p>
        <button
          onClick={onCancel}
          className="rounded-md border border-felt-line px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-chalk-dim hover:text-chalk"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="card space-y-4 p-6">
      <div className="mx-auto w-full max-w-[280px] space-y-4">
        <div
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="relative aspect-square w-full cursor-grab touch-none select-none overflow-hidden rounded-lg bg-felt active:cursor-grabbing"
        >
          {img && (
            <img
              src={img.src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: img.naturalWidth * scale,
                height: img.naturalHeight * scale,
                // Tailwind Preflight sets `img { max-width: 100% }`, which would
                // otherwise cap our deliberate width and break the framing math.
                maxWidth: 'none',
                transform: `translate(${offset.x}px, ${offset.y}px)`,
              }}
            />
          )}
          {/* A circular guide: the corners are dimmed so the round crop is
              obvious, but still visible so you can see what you're framing. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, transparent calc(50% - 1px), rgba(30,28,25,0.55) 50%)',
            }}
          />
          <div className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-chalk/30" />
        </div>

        <label className="flex items-center gap-3">
          <span className="font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
            Zoom
          </span>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => changeZoom(Number(e.target.value))}
            className="h-1 flex-1 cursor-pointer accent-brass"
            aria-label="Zoom"
          />
        </label>
      </div>

      <div className="flex items-center justify-end gap-3">
        {uploadError && (
          <p className="mr-auto font-mono text-xs text-blood">{uploadError}</p>
        )}
        <button
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-felt-line px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-chalk-dim hover:text-chalk disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={confirm}
          disabled={saving || !img}
          className="rounded-md bg-brass px-4 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save picture'}
        </button>
      </div>
    </div>
  );
}
